import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { fetchMyProfile, type MyProfile } from '../../data/accounts.ts';
import { toAppError } from '../../data/errors.ts';
import { clearAllOrderDrafts } from '../../data/orderDraft.ts';
import { supabase } from '../../data/supabase.ts';
import { disablePush } from '../../pwa/push.ts';
import { AuthContext, type AuthApi, type AuthState } from './auth-context.ts';

const PROFILE_KEY = 'servicioos-profile';

// Copia mínima del propio perfil para abrir el shell sin conexión; se borra al cerrar sesión.
function rememberProfile(profile: MyProfile) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // almacenamiento no disponible
  }
}

function recallProfile(userId: string): MyProfile | null {
  try {
    const stored = JSON.parse(localStorage.getItem(PROFILE_KEY) ?? 'null') as MyProfile | null;
    return stored && stored.id === userId ? stored : null;
  } catch {
    return null;
  }
}

function forgetProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch {
    // almacenamiento no disponible
  }
}

const NO_ACCESS = 'Tu cuenta está desactivada o ya no tiene acceso. Contacta al administrador.';

function signInMessage(code: string | undefined, status: number | undefined): string {
  if (code === 'user_banned') return 'Tu cuenta está desactivada. Contacta al administrador.';
  // Antes que el 400 genérico: Auth responde 400 también cuando falla la verificación de seguridad.
  if (code === 'captcha_failed') return 'No se pudo completar la verificación de seguridad. Inténtalo de nuevo.';
  if (code === 'invalid_credentials' || status === 400) return 'Correo o contraseña incorrectos.';
  if (code === 'over_request_rate_limit' || status === 429) {
    return 'Demasiados intentos de inicio de sesión desde esta conexión. Espera 5 minutos e inténtalo de nuevo.';
  }
  return 'No se pudo iniciar sesión. Revisa tu conexión e inténtalo de nuevo.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();
  const loading = useRef(false);

  const loadProfile = useCallback(
    async (silent: boolean) => {
      if (loading.current) return;
      loading.current = true;
      if (!silent) setState({ status: 'loading' });
      try {
        const profile = await fetchMyProfile();
        rememberProfile(profile);
        setState({ status: 'ready', profile });
      } catch (error) {
        const appError = toAppError(error);
        if (appError.code === 'permission') {
          // Cuenta desactivada o sin perfil: se cierra la sesión local y se limpian datos en memoria.
          forgetProfile();
          await supabase.auth.signOut({ scope: 'local' });
          queryClient.clear();
          setState({ status: 'signedOut', reason: NO_ACCESS });
        } else if (appError.code === 'network') {
          // Sin conexión con una sesión existente: se abre el shell con el último perfil conocido
          // para conservar borradores y adjuntos locales. Todo se revalida al volver la señal.
          const { data } = await supabase.auth.getSession();
          const cached = data.session ? recallProfile(data.session.user.id) : null;
          if (cached) setState({ status: 'ready', profile: cached });
          else if (!silent) setState({ status: 'error', error: appError });
        } else if (!silent) {
          setState({ status: 'error', error: appError });
        }
      } finally {
        loading.current = false;
      }
    },
    [queryClient],
  );

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session) void loadProfile(false);
      else setState({ status: 'signedOut' });
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      // No se llama a Supabase dentro del callback (puede bloquear el cliente); se difiere.
      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        setState((s) => (s.status === 'signedOut' ? s : { status: 'signedOut' }));
      } else if (event === 'SIGNED_IN') {
        setTimeout(() => void loadProfile(true), 0);
      }
    });
    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile, queryClient]);

  // Revalida rol y estado al volver a la app o recuperar la conexión.
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === 'visible' && state.status === 'ready') void loadProfile(true);
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('online', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('online', revalidate);
    };
  }, [loadProfile, state.status]);

  const api = useMemo<AuthApi>(
    () => ({
      state,
      signIn: async (email, password, captchaToken) => {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
          ...(captchaToken ? { options: { captchaToken } } : {}),
        });
        if (error) return signInMessage(error.code, error.status);
        await loadProfile(false);
        return null;
      },
      signOut: async () => {
        // Al cerrar sesión se retira el destino push de este dispositivo (§3).
        await disablePush().catch(() => undefined);
        forgetProfile();
        clearAllOrderDrafts();
        await supabase.auth.signOut({ scope: 'local' });
        queryClient.clear();
        setState({ status: 'signedOut' });
      },
      refreshProfile: () => loadProfile(true),
    }),
    [state, loadProfile, queryClient],
  );

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}
